<template>
  <div :class="['product-details-vehicle', { 'product-details-vehicle--is-selected': isVehicleSelected }]">
    <button :class="['product-details-vehicle__toggler', { 'product-details-vehicle__toggler--is-open': isOpen }]" @click="toggleAccordion()">
      <span v-if="!isVehicleSelected">
        {{ tStatic('checkProductFitsVehicle') }}
      </span>

      <span v-else>
        {{ tStatic('selectedProductFitsVehicle') }}
      </span>

      <oie-use-svg svg="circle-plus" color="#757272" :class="{'svg--45': isOpen}"></oie-use-svg>
    </button>

    <div :class="['product-details-vehicle__content', { 'product-details-vehicle__content--is-open': isOpen }]">
      <oie-base-dropdown
        v-model="selectedType"
        :initial-text="tStatic('filterSelectVehicleType')"
        :options="types"
        :is-large="false"
        :use-full-width="true"
        :use-thin-border="true"></oie-base-dropdown>

      <oie-base-dropdown
        v-model="selectedBrand"
        :initial-text="tStatic('filterSelectVehicleBrand')"
        :filter-input-placeholder="tStatic('dropdownFilterPlaceholder')"
        :options="brands"
        :is-large="false"
        :is-disabled="!selectedType"
        :use-filter-functionality="true"
        :use-wild-card-search="true"
        :use-full-width="true"
        :use-thin-border="true"></oie-base-dropdown>

      <oie-base-dropdown
        v-model="selectedModel"
        :initial-text="tStatic('filterSelectVehicleModel')"
        :options="models"
        :filter-input-placeholder="tStatic('dropdownFilterPlaceholder')"
        :is-large="false"
        :is-disabled="!selectedBrand"
        :use-filter-functionality="true"
        :use-full-width="true"
        :use-wild-card-search="true"
        :use-thin-border="true"></oie-base-dropdown>

      <a :href="selectedModelUrl" :class="['button button--with-border button--blue product-details-vehicle__redirect-action', { 'product-details-vehicle__redirect-action--is-disabled': !selectedModelUrl }]">{{ tStatic('selectModel') }}</a>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import accordion from '@/mixins/accordion.js'
import httpService from '@/core/http-service'

export default {
  mixins: [accordion],
  props: {
    model: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedType: null,
      selectedBrand: null,
      selectedModel: null,
      vehicles: [],
      preselectedModel: null
    }
  },
  computed: {
    types() {
      return this.vehicles.map(v => ({ text: v.name, value: v.id }))
    },
    brands() {
      const type = this.vehicles.find(v => v.id === this.selectedType)
      if (!type) { return [] }
      return type.brands.map(b => ({ text: b.name, value: b.id }))
    },
    models() {
      const type = this.vehicles.find(v => v.id === this.selectedType)
      if (!type) { return [] }
      const brand = type.brands.find(b => b.id === this.selectedBrand)
      if (!brand) { return [] }
      return brand.models.map(m => ({ text: m.name, value: m.id, url: m.url }))
    },
    selectedModelUrl() {
      const model = this.models.find(m => m.value === this.selectedModel)
      return model ? model.url : ''
    },
    isVehicleSelected() {
      return this.model.currentVehicle
    }
  },
  watch: {
    selectedType() {
      if (this.preselectedModel) {
        this.selectedBrand = this.preselectedModel.brandCode
      } else {
        this.selectedBrand = null
        this.selectedModel = null
      }
    },
    selectedBrand() {
      if (!this.preselectedModel) {
        this.selectedModel = null
      }
    },
    models() {
      if (this.preselectedModel) {
        this.selectedModel = this.preselectedModel.id
        this.preselectedModel = null
      }
    }
  },
  async mounted() {
    this.vehicles = (await httpService.get('fitsfor', { ...this.model })).data

    if (this.model.currentVehicle) {
      this.populateSelected(this.model.currentVehicle)
    }

    if (this.model.currentVehicle) {
      this.isOpen = false
    } else {
      this.isOpen = true
    }
  },
  methods: {
    populateSelected(currentVehicle) {
      const models = _.chain(this.vehicles).flatMap(p => p.brands).flatMap(b => b.models).value()
      const selected = models.find(p => p.modelCode === currentVehicle)
      if (!selected) return
      this.preselectedModel = selected
      this.selectedType = selected.vehicleType
    }
  }
}
</script>

<style lang="scss">
.product-details-vehicle {
  background-color: color(background-yellow);
  border: 1px solid color(border-yellow);
}

.product-details-vehicle--is-selected {
  background-color: color(background-green-4);
  border: 1px solid color(border-green-3);
}
// this styling ensures that the borders of the dynamic sections seems to collapse. Suboptimal code, should be refactored for better redability and maintainability
.product-details-vehicle ~ .product-actions_right-container-inner .product-actions,
.mandatory-comment ~ .product-actions_right-container-inner .product-actions,
.product-details-vehicle ~ .mandatory-comment
{
  border-top: none;
}

.product-details-vehicle__toggler {
  @include reset-button;
  @include body-semi-bold;
  width: 100%;
  padding: measure(quarter) measure(half);
  overflow: hidden;

  @include media(medium) {
    padding: measure(half) measure(full);
  }
}

.product-details-vehicle__toggler--is-open {
  margin-bottom: measure(quarter);
}

.product-details-vehicle__toggler > span {
  margin-right: measure(quarter);
  text-align: left;
}

.product-details-vehicle__toggler > svg {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  margin-left: auto;
  transition: transform 140ms;
}

.product-details-vehicle__content {
  display: none;
  padding-top: 0;
  padding-right: measure(half);
  padding-bottom: measure(quarter);
  padding-left: measure(half);

  @include media(medium) {
    padding-top: 0;
    padding-right: measure(full);
    padding-bottom: measure(half);
    padding-left: measure(full);
  }
}

.product-details-vehicle__content--is-open {
  display: flex;
  flex-direction: column;
}

.product-details-vehicle__content > * {
  flex-grow: 1;
  max-width: initial;
  margin-bottom: measure(quarter);
}

.product-details-vehicle__content > *:nth-child(3) {
  margin-bottom: measure(half);
}

.product-details-vehicle__content .dropdown__options {
  z-index: 4;
}

.product-details-vehicle__redirect-action {
  margin-bottom: 0;

  @include media(small) {
    align-self: flex-end;
  }
}

.product-details-vehicle__redirect-action--is-disabled {
  pointer-events: none;
  opacity: .5;
}
</style>
